import Vue from 'vue'
import api from '@/apis/site'
import parser, { parsePreAuthorize } from '@/models/site'
import {
  RESET_STATE,
  PROCESS_API_SUCCESS,
  PROCESS_API_FAILURE,
  INIT_SELECTED_SITE,
  SELECT_SITE,
  GET_SITE_REQUEST,
  GET_SITE_SUCCESS,
  GET_SITE_FAILURE,
  GET_SITES_REQUEST,
  GET_SITES_SUCCESS,
  GET_SITES_FAILURE,
  SET_SITE_PRE_AUTHORIZE,
} from '@/store/mutation-types'

let initialState = {
  sites: {},
  siteIds: [],
  selectedSiteId: '',
  status: {
    getSite: null,
    getSites: null,
  },
}

// initial state
const state = Vue.util.extend({}, initialState)

// getters
const getters = {
  site: function (state) {
    return function (siteId) {
      return state.sites[siteId]
    }
  },
  sites: function (state) {
    return state.sites
  },
  siteIds: function (state) {
    return state.siteIds
  },
  statusGetSite: function (state) {
    return state.status.getSite
  },
  statusGetSites: function (state) {
    return state.status.getSites
  },
  selectedSiteId: function (state) {
    return state.selectedSiteId
  },
  isSiteArmingScheduleEnabled: function (state, getters) {
    return function (siteId) {
      const site = getters.site(siteId)
      if (!(site instanceof Object)) return false
      if (!(site.statusInfo instanceof Object)) return false
      const isEnabled = site.statusInfo.armingScheduleEnabled
      return isEnabled ? isEnabled : false
    }
  },
  siteTimezone: function (state, getters) {
    return function (siteId) {
      const site = getters.site(siteId)
      if (!(site instanceof Object)) return "UTC"
      if (!(site.locationInfo instanceof Object)) return "UTC"
      const timezone = site.locationInfo.timezone
      return timezone ? timezone : "UTC"
    }
  },
  isPartitionEnabled: function (state, getters) {
    return function (siteId) {
      const site = getters.site(siteId)
      if (!(site instanceof Object)) return false
      return !!site.isPartitionEnabled
    }
  },
  showCameraModel: function (state, getters) {
    return function (siteId) {
      const site = getters.site(siteId)
      if (!(site instanceof Object)) return false
      return !!site?.statusInfo?.showCameraModel
    }
  },
  showPartitionUI: function (state, getters) {
    return function (siteId) {
      if (!getters.isPartitionEnabled(siteId)) return false
      const members = getters.partitionMembers
      if (!Array.isArray(members)) return false
      const myUserId = getters.myUserId
      const count = new Set(
        members.filter(m => Number(m.user_id) === Number(myUserId)).map(m => m.partition_id)
      ).size
      return count > 1
    }
  },
  // timezone기준을 locale로 할것인지 site로 할것인지 여기에 정의한다.
  siteDisplayTimezone: function (state, getters) {
    return function (siteId) {
      const site = getters.site(siteId)
      if (!(site instanceof Object)) return "UTC"
      if (!(site.locationInfo instanceof Object)) return "UTC"
      const timezone = site.locationInfo.timezone
      return timezone ? timezone : "UTC"
    }
  },
}

// actions
const actions = {
  getSite: function ({commit}, {siteId}) {
    commit(GET_SITE_REQUEST)
    return new Promise((resolve, reject) => {
      api.getSite({siteId}).then(
        res => {
          let data = res.body[0]
          commit(GET_SITE_SUCCESS, {data})
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  // Owner Pre-Authorize toggles (CHEKT-2084). PUT the changed keys; the 200 body
  // is the full 5-key set → commit it back into the site's approvalConfig.
  updateSiteApprovalSettings: function ({commit}, {siteId, changes}) {
    return api.updateApprovalSettings({siteId, changes}).then(res => {
      const preAuthorize = parsePreAuthorize((res && res.body) || {})
      commit(SET_SITE_PRE_AUTHORIZE, {siteId, preAuthorize})
      return preAuthorize
    })
  },
  getSites: function ({commit, dispatch}) {
    commit(GET_SITES_REQUEST)
    return new Promise((resolve, reject) => {
      api.getSites().then(
        res => {
          let data = res.body
          commit(GET_SITES_SUCCESS, {data})
          dispatch('initSelectedSite')
          resolve()
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITES_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  initSelectedSite: async function ({commit, state}) {
    const idInStorage = await Vue.tool.storageGetItem('selectedSiteId')
    if (state.sites[idInStorage]) {
      commit(INIT_SELECTED_SITE, {siteId: idInStorage})
      return
    }
    const siteIds = Object.keys(state.sites)
    if (siteIds.length < 1) {
      commit(INIT_SELECTED_SITE, {siteId: ''})
      return
    }
    commit(INIT_SELECTED_SITE, {siteId: siteIds[0]})
  },
}

// selectedSiteId 정규화. null/undefined는 빈 문자열로 — String(null)이 'null'이 되는 것을 막는다.
function normalizeSiteId (siteId) {
  return siteId == null ? '' : String(siteId)
}

// mutations
const mutations = {
  [RESET_STATE]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  // selectedSiteId는 항상 String으로 정규화한다.
  // 부팅 경로(INIT_SELECTED_SITE)는 storage 값이나 Object.keys()에서 오므로 원래 String이지만,
  // 사이트 전환 경로(SELECT_SITE)는 API의 site_id(숫자)를 그대로 받는다. 그 결과 전환 후에는
  // selectedSiteId가 Number가 되어, 레코드의 siteId를 String으로 스탬프해 두고 엄격 비교하는
  // 사이트 스코프 getter들(bypass/tempDisarm의 records·requests·count 등)이 전부 빈 값이 됐다
  // — 승인 대기 칩이 사이트를 옮겼다 돌아오면 사라지고, 리로드해야만 살아나던 원인.
  [INIT_SELECTED_SITE]: function (state, {siteId}) {
    // if already selected same id before.. ignore
    // because.. prevent for extra selectedSiteId watch action
    if (state.selectedSiteId + '' === siteId + '') return

    // add id
    state.selectedSiteId = normalizeSiteId(siteId)
  },
  [SELECT_SITE]: function (state, {siteId}) {
    if (!state.sites[siteId]) return
    state.selectedSiteId = normalizeSiteId(siteId)
    Vue.tool.storageSetItem('selectedSiteId', siteId + '')
  },
  [GET_SITE_REQUEST]: function (state) {
    state.status.getSite = "requested"
  },
  [GET_SITE_SUCCESS]: function (state, {data}) {
    let siteInfo = parser.parseSiteData(data)

    if (siteInfo.siteId) {
      state.sites = {...state.sites, [siteInfo.siteId]: siteInfo}
    }
    state.status.getSite = "successful"
  },
  [GET_SITE_FAILURE]: function (state) {
    state.status.getSite = "failed"
  },
  [SET_SITE_PRE_AUTHORIZE]: function (state, {siteId, preAuthorize}) {
    const site = state.sites[siteId]
    if (!site || !site.approvalConfig) return
    const nextSite = { ...site, approvalConfig: { ...site.approvalConfig, preAuthorize } }
    state.sites = { ...state.sites, [siteId]: nextSite }
  },
  [GET_SITES_REQUEST]: function (state) {
    state.status.getSites = "requested"
  },
  [GET_SITES_SUCCESS]: function (state, {data}) {
    let sites = {}
    let siteIds = []
    data.map(siteData => {
      let siteInfo = parser.parseSiteData(siteData)
      if (siteInfo.siteId) {
        sites[siteInfo.siteId] = siteInfo
        siteIds.push(siteInfo.siteId)
      }
    })
    Vue.set(state, 'sites', sites)
    Vue.set(state, 'siteIds', siteIds)
    state.status.getSites = "successful"
  },
  [GET_SITES_FAILURE]: function (state) {
    state.status.getSites = "failed"
  },
}

export default {
  state,
  getters,
  actions,
  mutations
}
